/**
 * iter149 — Record Payment sub-modal.
 *
 * Immutable ledger entry — every call creates a NEW payment_receipt
 * row. Corrections must go through the adjustment flow (out of scope
 * for this modal — see FinancialCenterModal for future adjustment UI).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatApiError, inr } from "@/lib/utils";
import { X, Receipt, Loader2, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const MODES = [
  { key: "cash", label: "Cash" },
  { key: "upi", label: "UPI" },
  { key: "bank", label: "Bank Transfer" },
  { key: "cheque", label: "Cheque" },
  { key: "card", label: "Card" },
  { key: "other", label: "Other" },
];

export default function RecordPaymentModal({ open, booking, party, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState("upi");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [proofUrl, setProofUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setAmount("");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setMode("upi");
    setReference("");
    setNotes("");
    setProofUrl("");
    setErr("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  const amt = Number(amount) || 0;
  const overpay = party && amt > (party.pending + 0.5);
  const canSave = amt > 0 && paymentDate && mode && !busy;

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.post(`/bookings/${booking.id}/payments`, {
        party_id: party.id,
        amount: amt,
        payment_date: paymentDate,
        mode,
        reference: reference.trim(),
        notes: notes.trim(),
        proof_url: proofUrl.trim(),
      });
      toast.success(`Recorded ${inr(amt)} · ${party.name}`);
      onSaved?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open || !party || !booking) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 kb-fade-in p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Record payment for ${party.name}`}
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="record-payment-modal"
      >
        <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-emerald-700" strokeWidth={2} />
            <div>
              <div className="font-heading font-semibold">Record Payment</div>
              <div className="text-[11px] uppercase tracking-widest text-stone-500">{party.name}</div>
            </div>
          </div>
          <button className="btn-ghost p-1 rounded" onClick={onClose} aria-label="Close" data-testid="record-payment-close">
            <X className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-3 gap-2 text-xs bg-stone-50 border border-stone-100 rounded p-2">
            <div>
              <div className="text-[9px] uppercase tracking-widest text-stone-500 font-semibold">Assigned</div>
              <div className="font-mono">{inr(party.assigned)}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-emerald-700 font-semibold">Received</div>
              <div className="font-mono text-emerald-800">{inr(party.paid)}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-rose-700 font-semibold">Pending</div>
              <div className="font-mono text-rose-800">{inr(party.pending)}</div>
            </div>
          </div>

          <div>
            <label className="editorial-eyebrow block mb-1" htmlFor="rp-amount">Amount Received <span className="text-rose-700">*</span></label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none">₹</span>
              <input
                id="rp-amount"
                type="number"
                min={0}
                className="input pl-6 text-right font-mono"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                data-testid="record-payment-amount"
              />
            </div>
            {overpay && (
              <div className="mt-1 text-[11px] text-amber-700 flex items-start gap-1">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                <span>Exceeds pending amount by {inr(amt - party.pending)}. The extra will still be recorded.</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="editorial-eyebrow block mb-1" htmlFor="rp-date">Payment Date <span className="text-rose-700">*</span></label>
              <input
                id="rp-date"
                type="date"
                className="input"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                data-testid="record-payment-date"
              />
            </div>
            <div>
              <label className="editorial-eyebrow block mb-1" htmlFor="rp-mode">Payment Mode <span className="text-rose-700">*</span></label>
              <select
                id="rp-mode"
                className="input"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                data-testid="record-payment-mode"
              >
                {MODES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="editorial-eyebrow block mb-1" htmlFor="rp-ref">Reference / UTR</label>
            <input
              id="rp-ref"
              type="text"
              className="input"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Transaction ID, UTR, cheque no…"
              maxLength={120}
              data-testid="record-payment-reference"
            />
          </div>

          <div>
            <label className="editorial-eyebrow block mb-1" htmlFor="rp-notes">Notes</label>
            <textarea
              id="rp-notes"
              rows={2}
              className="input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Advance received via Google Pay"
              maxLength={500}
              data-testid="record-payment-notes"
            />
          </div>

          <div>
            <label className="editorial-eyebrow block mb-1" htmlFor="rp-proof">Proof URL (optional)</label>
            <input
              id="rp-proof"
              type="url"
              className="input"
              value={proofUrl}
              onChange={(e) => setProofUrl(e.target.value)}
              placeholder="Paste receipt image / drive link"
              maxLength={500}
              data-testid="record-payment-proof"
            />
            <div className="text-[10px] text-stone-400 mt-0.5">Direct file upload coming soon — paste a shareable link for now.</div>
          </div>

          {err && (
            <div className="card p-2 bg-rose-50 border-rose-200 flex items-start gap-2" role="alert">
              <AlertTriangle className="w-3.5 h-3.5 text-rose-700 mt-0.5 shrink-0" strokeWidth={2} />
              <div className="text-xs text-rose-900">{err}</div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-stone-200 bg-stone-50/60 flex items-center justify-end gap-2">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={!canSave}
            onClick={save}
            data-testid="record-payment-save"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={2.2} />}
            Save Payment
          </button>
        </div>
      </div>
    </div>
  );
}
